import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApproveSalaryAdvanceDTO,
  CreateSalaryAdvanceDTO,
  ListSalaryAdvancesQuery,
  RejectSalaryAdvanceDTO,
} from "@/modules/hr/salary-advance/domain/contracts/types";
import { salaryAdvanceApi } from "./client";

const ADVANCE_KEY = ["hr", "salary-advance"] as const;

const keys = {
  list: (q: ListSalaryAdvancesQuery) => [...ADVANCE_KEY, "list", q] as const,
  detail: (id: string) => [...ADVANCE_KEY, "detail", id] as const,
  myList: (q: Omit<ListSalaryAdvancesQuery, "employeeId">) =>
    [...ADVANCE_KEY, "me", "list", q] as const,
  myDetail: (id: string) => [...ADVANCE_KEY, "me", "detail", id] as const,
};

// ─── Admin ───

export function useSalaryAdvancesQuery(
  query: ListSalaryAdvancesQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => salaryAdvanceApi.list(query),
    enabled: options?.enabled ?? true,
  });
}

export function useSalaryAdvanceDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.detail(id ?? ""),
    queryFn: () => salaryAdvanceApi.getById(id!),
    enabled: !!id,
  });
}

export function useApproveSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ApproveSalaryAdvanceDTO }) =>
      salaryAdvanceApi.approve(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADVANCE_KEY });
    },
  });
}

export function useRejectSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RejectSalaryAdvanceDTO }) =>
      salaryAdvanceApi.reject(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADVANCE_KEY });
    },
  });
}

export function useMarkPaidSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => salaryAdvanceApi.markPaid(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADVANCE_KEY });
    },
  });
}

export function useCreateAdvanceForEmployeeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateSalaryAdvanceDTO) =>
      salaryAdvanceApi.createForEmployee(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADVANCE_KEY });
    },
  });
}

// ─── Employee Self-Service ───

export function useMyAdvancesQuery(
  query: Omit<ListSalaryAdvancesQuery, "employeeId">,
) {
  return useQuery({
    queryKey: keys.myList(query),
    queryFn: () => salaryAdvanceApi.listMine(query),
  });
}

export function useMyAdvanceDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.myDetail(id ?? ""),
    queryFn: () => salaryAdvanceApi.getMyById(id!),
    enabled: !!id,
  });
}

export function useCreateSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: Omit<CreateSalaryAdvanceDTO, "employeeId">) =>
      salaryAdvanceApi.createRequest(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADVANCE_KEY });
    },
  });
}

export function useCancelSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => salaryAdvanceApi.cancelRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADVANCE_KEY });
    },
  });
}
